import React, {useState} from 'react'
import {useLocation, useParams} from 'react-router-dom'
import {MembersLeaveBucketPageLocationState} from '../../../Props/MembersLeaveBucketPageLocationStates'
import {NavigatorForMemberPage} from '../../../Navigator/NavigatorForMemberPage'
import {MBMemberError} from '../../../Common/MBMemberError'
import {useMBMemberError} from '../../Hooks/UseMBMemberError'
import MemberRepo from '../../../Repo/MemberRepo'
import {MBMemberInput} from '../../Atoms/MBMemberInput'
import {animateScroll} from 'react-scroll'
import {CommonResponseStatus} from '../../../../Network/CommonResponse'
import {MembersPageTemplate} from '../../Molecules/BaseMembersPages/MembersPageTemplate'
import {PageTitle} from '../../Molecules/BaseMembersPages/PageTitle'
import {ErrorSummary} from '../../Molecules/ErrorsAndAlert/ErrorSummary/ErrorSummary'
import StepGuide from '../../Molecules/StepGuide/StepGuide'
import {ContentBlock} from '../../Molecules/BaseContentBlocks/ContentBlock'
import {ContentTitle} from '../../Molecules/BaseContentBlocks/ContentTitle'
import {ConfirmContent} from '../../Molecules/BaseContentBlocks/ConfirmContent'
import {AlertList} from '../../Molecules/ErrorsAndAlert/AlertList'
import {ErrorDisplay} from '../../Molecules/ErrorsAndAlert/ErrorDisplay'
import {MessageForAction} from '../../Molecules/BaseMembersPages/MessageForAction'
import {PIMSecondaryButton} from '../../Atoms/Buttons/Bases/PIMSecondaryButton'
import {PIMSubmitButton} from '../../Atoms/Buttons/Bases/PIMSubmitButton'
import {PIMPageActions} from '../../Atoms/Layouts/PIMPageActions'
import {PIMStack} from '../../Atoms/Layouts/Bases/PIMStack'
import {useAsync} from 'react-use'
import {MembersMobileCarrierMaintenancePage} from '../Error/MembersMobileCarrierMaintenancePage'
import {CvsProcedureOrCompletedErrorPage} from './Components/CvsProcedureOrCompletedErrorPage'
import {LeavingMessageContent} from "../../Components/LeavingMessageContent"

type ErrorState = {
  isInputError: boolean
  agreeCheckErrors: string[]
}

type MembersLeaveBucketConfirmPageProps = {
  navigator: NavigatorForMemberPage
  memberRepo: MemberRepo
}

export const MembersLeaveBucketConfirmPage: React.VFC<MembersLeaveBucketConfirmPageProps>
  = (props) => {
  const location = useLocation<MembersLeaveBucketPageLocationState>()
  const locationState: MembersLeaveBucketPageLocationState = location.state

  if (isInvalidLocationState(locationState)) {
    throw new MBMemberError('InvalidOperation')
  }

  const {bucketKey} = useParams<{ bucketKey: string }>()

  const [processing, setProcessing] = useState<boolean>(false)
  const [agreeCheck, setAgreeCheck] = useState<boolean>(false)
  const [errorState, setErrorState] = useState<ErrorState>({
    isInputError: false,
    agreeCheckErrors: [],
  })
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const [mobileCarrierMaintenance, setMobileCarrierMaintenance] = useState<boolean>(false)
  const [cvsProcedureOrCompletedError, setCvsProcedureOrCompletedError] = useState<boolean>(false)
  const [freeBucketLeaveProcessing, setFreeBucketLeaveProcessing] = useState<boolean>(false)
  const {bucketInfo, bucketLeaveReason, nowContract} = locationState
  const pageTitle = `${bucketInfo?.name} 退会確認`

  const throwMBMemberError = useMBMemberError()

  const getBucketLeaveByBucketKeyResult = useAsync(async () => {
    return await props.memberRepo.getBucketLeaveByBucketKey(bucketKey)
  })

  if (getBucketLeaveByBucketKeyResult.loading) {
    return null
  }

  if (getBucketLeaveByBucketKeyResult.error) {
    throw new MBMemberError(getBucketLeaveByBucketKeyResult.error.message)
  }

  if (!getBucketLeaveByBucketKeyResult.value) {
    throw new MBMemberError('UnknownError')
  }

  switch (getBucketLeaveByBucketKeyResult.value.status) {
    case CommonResponseStatus.Ok:
      break
    case CommonResponseStatus.NotFound:
      throw new MBMemberError('NotFound')
    case CommonResponseStatus.OvertimeError:
      return (
        <MembersMobileCarrierMaintenancePage
          pageTitle={pageTitle}
          operationName={'退会手続き'}
          bucketInfo={bucketInfo}
          buttonName={bucketInfo?.fcMyPageInfo.name}
          url={bucketInfo?.fcMyPageInfo.url}
        />
      )
    case CommonResponseStatus.Forbidden:
      if (isCvsProcedureOrCompletedError(getBucketLeaveByBucketKeyResult.value.errorMessageList)) {
        return (
          <CvsProcedureOrCompletedErrorPage
            bucketInfo={bucketInfo}
            pageTitle={pageTitle}
            errorSummaryHeadLine={getBucketLeaveByBucketKeyResult.value.errorMessageList[0]}
            errorSummaryDetail={getBucketLeaveByBucketKeyResult.value.errorMessageList[1]}
          />
        )
      }
      throw new MBMemberError('NotFound')
    case CommonResponseStatus.InternalServerError:
      throw new MBMemberError('ServerError')
    case CommonResponseStatus.NoAuthority:
      throw new MBMemberError('SystemError')
    case CommonResponseStatus.Processing:
      if (nowContract.contractMethod.method === 'none') {
        return (
          <LeavingMessageContent
            pageTitle={pageTitle}
            bucketInfo={bucketInfo}
          />
        )
      }
      throw new MBMemberError('SystemError')
    default:
      throw new MBMemberError('UnknownError')
  }

  if (!locationState.bucketLeave) {
    throw new MBMemberError('SystemError')
  }
  const leaveAttentionList = locationState.bucketLeave.leaveAttentionList

  const goToLeaveBucketCompletePage = async (): Promise<void> => {
    setProcessing(true)
    try {
      if (!agreeCheck) {
        setProcessing(false)
        setErrorState({
          isInputError: true,
          agreeCheckErrors: ['チェックが必要です。'],
        })
        animateScroll.scrollToTop()
        return
      }

      if (!bucketInfo) {
        // noinspection ExceptionCaughtLocallyJS
        throw new MBMemberError('SystemError')
      }

      const response = await props.memberRepo.putBucketForLeave(
        bucketInfo.key,
        bucketLeaveReason,
      )

      switch (response.status) {
        case CommonResponseStatus.Ok:
          props.navigator.goToMembersLeaveBucketCompletePage(locationState)
          break
        case CommonResponseStatus.BadRequest:
          setProcessing(false)
          setErrorMessages(response.errorMessageList)
          animateScroll.scrollToTop()
          return
        case CommonResponseStatus.NotFound:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('NotFound')
        case CommonResponseStatus.OvertimeError:
          setMobileCarrierMaintenance(true)
          break
        case CommonResponseStatus.Forbidden:
          // eslint-disable-next-line max-depth
          if (isCvsProcedureOrCompletedError(response.errorMessageList)) {
            setCvsProcedureOrCompletedError(true)
            setErrorMessages(response.errorMessageList)
            break
          }
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('SystemError')
        case CommonResponseStatus.InternalServerError:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('ServerError')
        case CommonResponseStatus.NoAuthority:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('SystemError')
        case CommonResponseStatus.Processing:
          // eslint-disable-next-line max-depth
          if (nowContract.contractMethod.method === 'none') {
            setFreeBucketLeaveProcessing(true)
            break
          }
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('SystemError')
        default:
          // noinspection ExceptionCaughtLocallyJS
          throw new MBMemberError('UnknownError')
      }

    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'SystemError'
      throwMBMemberError(errorMessage)
    }
  }

  return (<>
    {mobileCarrierMaintenance && (
      <MembersMobileCarrierMaintenancePage
        pageTitle={pageTitle}
        operationName={'退会手続き'}
        bucketInfo={bucketInfo}
        buttonName={bucketInfo?.fcMyPageInfo.name}
        url={bucketInfo?.fcMyPageInfo.url}
      />
    )}
    {cvsProcedureOrCompletedError && (
      <CvsProcedureOrCompletedErrorPage
        bucketInfo={bucketInfo}
        pageTitle={pageTitle}
        errorSummaryHeadLine={errorMessages[0]}
        errorSummaryDetail={errorMessages[1]}
      />
    )}
    {freeBucketLeaveProcessing && (
      <LeavingMessageContent
        pageTitle={pageTitle}
        bucketInfo={bucketInfo}
      />
    )}
    {!mobileCarrierMaintenance && !cvsProcedureOrCompletedError && !freeBucketLeaveProcessing && (
      <MembersPageTemplate
        hideFooterNavi={true}
        bucketInfo={bucketInfo}
      >
        <PageTitle>{pageTitle}</PageTitle>

        <ErrorSummary
          display={errorState.isInputError}
          headline={'『内容を確認しました』にチェックが入っていません。'}
        />

        <StepGuide activity={'leave'} current={'confirm'}/>

        <ContentBlock data-testid={'block-leave-confirm'}>
          <PIMStack spacing={'18px'}>
            <ContentTitle noMarginBottom={true}>退会の確認</ContentTitle>
            <ConfirmContent
              label={'退会の理由'}
              values={[bucketLeaveReason]}
            />
          </PIMStack>

          <AlertList
            headline={'ご確認ください'}
            alertMessages={leaveAttentionList}
          />

          <p className="align_c mb_15">
            上記内容をご確認のうえ、<br/>下のチェックボックスにチェックを入れてください。
          </p>
          <p className="align_c mb_10">
            <label>
              <MBMemberInput
                className="form-check_01"
                type="checkbox"
                hasError={errorState.agreeCheckErrors.length > 0}
                checked={agreeCheck}
                onChange={(): void => setAgreeCheck(!agreeCheck)}
              />
              <span className="txt">内容を確認しました</span>
            </label>
          </p>
          <ErrorDisplay
            errors={errorState.agreeCheckErrors}
            style={{textAlign: 'center'}}
          />

        </ContentBlock>

        <MessageForAction>
          上記の内容に間違いがなければ<br/>『本当に退会する』ボタンを押してください。
        </MessageForAction>

        <PIMStack alignItems={'center'}>
          <ErrorDisplay errors={errorMessages}/>
        </PIMStack>

        <PIMPageActions>
          <PIMSubmitButton
            testId={'really-withdraw-button'}
            disabled={processing}
            onClick={(): Promise<void> => goToLeaveBucketCompletePage()}
          >
            本当に退会する
          </PIMSubmitButton>
          <PIMSecondaryButton onClick={(): void => props.navigator.goBack()}>
            退会理由の入力に戻る
          </PIMSecondaryButton>
        </PIMPageActions>

      </MembersPageTemplate>
    )}
  </>)
}

const isInvalidLocationState = (
  locationState: MembersLeaveBucketPageLocationState,
): boolean => {
  if (!locationState) {
    return true
  }
  if (!locationState.bucketInfo) {
    return true
  }
  if (!locationState.bucketLeave) {
    return true
  }
  if (locationState.bucketLeaveReason == null) {
    return true
  }
  if (!locationState.nowContract) {
    return true
  }
  if (locationState.bucketLeave.leavePageTopMessage === undefined ||
    locationState.bucketLeave.leaveAttentionList === undefined) {
    return true
  }
  return false
}

const isCvsProcedureOrCompletedError = (errorMessageList: string[]): boolean => {
  return errorMessageList.length === 2
}
